import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import consola from 'consola'

/**
 * Плагин для инициализации push-уведомлений только для авторизованных пользователей
 * Работает только на Android
 * Для iOS используется отдельный плагин: push-notifications-ios.client.ts
 */
export default defineNuxtPlugin(async () => {
  const logPrefix = '[PUSH-NOTIFICATIONS-Android]';
  const log = (...args: any[]) => consola.log(logPrefix, ...args);

  // Работаем только на Android
  if (!process.client || Capacitor.getPlatform() !== 'android') {
    return;
  }

  log('Android push notifications plugin loaded, waiting for user authentication...');

  // Флаг для предотвращения повторной инициализации
  let isInitialized = false;

  /**
   * Хэндлы слушателей, которые повесил ЭТОТ плагин. Снимаем только их: обработчик тапа
   * по уведомлению живёт в layouts/default.vue, и он один на обе платформы.
   */
  let ownListeners: PluginListenerHandle[] = [];

  const removeOwnListeners = async (): Promise<void> => {
    const handles = ownListeners;
    ownListeners = [];
    // filter: падение здесь оставило бы инициализацию незавершённой, то есть без push
    await Promise.all(handles.filter(Boolean).map((handle) => handle.remove()));
  };

  // Глобальная функция для сброса состояния инициализации (для повторного включения)
  globalThis.resetPushNotificationsState = () => {
    log('Resetting push notifications state...');
    isInitialized = false;
  };

  // Глобальная функция для проверки состояния инициализации (для отладки)
  globalThis.getPushNotificationsState = () => {
    return {
      isInitialized,
      platform: Capacitor.getPlatform(),
      isNativePlatform: Capacitor.isNativePlatform(),
    };
  };

  // Глобальная функция для инициализации push notifications после авторизации
  globalThis.initPushNotificationsAfterAuth = async () => {
    if (isInitialized) {
      log('Push notifications already initialized, skipping...');
      return true;
    }

    log('Initializing push notifications after authentication...');

    try {
      // Проверяем и запрашиваем разрешения
      const permissionStatus = await PushNotifications.checkPermissions();
      log('Current permission status:', permissionStatus.receive);

      if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
        log('Requesting push notification permissions...');
        const requestResult = await PushNotifications.requestPermissions();
        log('Permission request result:', requestResult.receive);

        if (requestResult.receive !== 'granted') {
          log('Push notification permissions denied');
          return false;
        }
      } else if (permissionStatus.receive === 'denied') {
        log('Push notification permissions denied');
        return false;
      }

      // Снимаем ТОЛЬКО свои слушатели, а не все подряд.
      //
      // `removeAllListeners()` убивал и обработчик тапа по уведомлению из
      // layouts/default.vue — а он там единственный на обе платформы. Инициализация
      // вызывается ПОСЛЕ маунта лейаута (её дёргает вотчер на профиль при каждом
      // холодном старте у залогиненного гостя), поэтому тап переставал работать вовсе.
      log('Removing own listeners...');
      await removeOwnListeners();

      // Одноразовый промис, отражающий РЕАЛЬНЫЙ итог регистрации.
      // PushNotifications.register() резолвится сразу (только запускает регистрацию),
      // поэтому ждём событие 'registration' (токен выдан) либо 'registrationError'
      // (FCM не выдал токен). Без этого UI считал push включённым, хотя токен не пришёл.
      let settleRegistration: ((ok: boolean) => void) | null = null;
      const registrationOutcome = new Promise<boolean>((resolve) => {
        settleRegistration = resolve;
        // Страховка: если ни одно событие не пришло — считаем неудачей
        setTimeout(() => {
          if (settleRegistration) {
            log('Registration timed out without token');
            settleRegistration(false);
            settleRegistration = null;
          }
        }, 15_000);
      });

      // Настраиваем обработчики событий ПЕРЕД регистрацией
      log('Setting up listeners...');
      const remember = async (handle: Promise<PluginListenerHandle>): Promise<void> => {
        ownListeners.push(await handle);
      };

      await remember(PushNotifications.addListener('registration', async (token) => {
        log('Push registration successful, token:', token.value.slice(0, 20) + '...');

        // Токен получен — регистрация реально удалась (ошибки отправки на сервер ниже не критичны)
        if (settleRegistration) {
          settleRegistration(true);
          settleRegistration = null;
        }

        try {
          // Сохраняем токен в storage для управления настройками
          const storageModule = await import('@features/notifications');
          const storage = storageModule.usePushNotificationStorage();
          await storage.setPushToken(token.value);

          // Используем прямой импорт API вместо $api
          const apiProfile = await import('~/entities/user/api');
          const tokenData = {
            token: token.value,
            platform: Capacitor.getPlatform(),
            // deviceId и deviceName - опциональные поля, не отправляем пока @capacitor/device не установлен
          };
          
          log('📤 Sending token to server:', {
            tokenPreview: token.value.slice(0, 30) + '...',
            platform: tokenData.platform,
          });

          await apiProfile.default.addPushToken(tokenData);
          log('✅ Token saved to server successfully');
        } catch (error) {
          consola.error(logPrefix, 'Error saving token to server:', error);
        }
      }));

      await remember(PushNotifications.addListener('registrationError', (error) => {
        // error приходит как объект и в логах выглядит как «[object Object]» — раскрываем поля.
        // Типичная причина «AUTHENTICATION_FAILED» — проблема конфигурации Firebase (см. ниже),
        // а не фронтенда: невалидный/ограниченный API-ключ в google-services.json,
        // отключённый Firebase Installations / Cloud Messaging API в проекте, либо устаревший
        // google-services.json (несовпадение проекта/ключа после ротации).
        const err = error as { error?: unknown; message?: unknown; code?: unknown };
        consola.error(logPrefix, 'Push registration error:', {
          message: err?.message ?? err?.error ?? String(error),
          code: err?.code,
          raw: error,
        });

        // Регистрация провалилась — сообщаем вызывающему коду реальный результат
        if (settleRegistration) {
          settleRegistration(false);
          settleRegistration = null;
        }
      }));

      await remember(PushNotifications.addListener('pushNotificationReceived', (notification) => {
        log('Push notification received:', notification.title);
        // Здесь можно добавить дополнительную обработку полученных уведомлений
      }));

      await remember(PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        log('Push notification action performed:', action.actionId);
        // Навигацию по тапу НЕ добавлять сюда: обработчик уже висит в
        // layouts/default.vue и работает на обеих платформах. Второй слушатель
        // приведёт к двойному выполнению действия (так уже было на iOS).
      }));

      // Если разрешение уже было получено ранее, проверяем наличие сохраненного токена
      if (permissionStatus.receive === 'granted') {
        log('Permission already granted, checking for existing token...');
        try {
          const storageModule = await import('@features/notifications');
          const storage = storageModule.usePushNotificationStorage();
          const existingToken = await storage.getPushToken();
          
          if (existingToken) {
            log('Found existing token in storage:', existingToken.slice(0, 20) + '...');
            log('Re-sending token to server...');
            
            const apiProfile = await import('~/entities/user/api');
            const tokenData = {
              token: existingToken,
              platform: Capacitor.getPlatform(),
            };
            
            await apiProfile.default.addPushToken(tokenData);
            log('✅ Existing token re-sent to server successfully');
          } else {
            log('No existing token found in storage');
          }
        } catch (error) {
          log('Error checking existing token:', error);
        }
      }

      // Регистрируем push-уведомления (получим токен если его нет или он изменился)
      log('Registering for push notifications...');
      await PushNotifications.register();

      // Ждём реальный итог: токен получен или ошибка регистрации
      const registered = await registrationOutcome;
      isInitialized = registered;
      log(registered ? 'Push notifications initialized successfully' : 'Push registration failed (no token issued)');
      return registered;
    } catch (error) {
      consola.error(logPrefix, 'Failed to initialize push notifications:', error);
      return false;
    }
  };

  log('Push notifications plugin ready');
});
